// What Chat is asked when someone asks it about the shopping assistant's conversations: fixed
// words, with the store and conversation attached as a reference (a chip, not words in the
// message). Chat reads what shoppers wrote itself, with its storefront_conversations tool, as
// data; a shopper's words are never put in the member's own message. Words come from the
// `storefront` namespace.
import { formatCopy } from '@/i18n/ns';
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';
import type { StorefrontConversations } from '../types';
import type { OverviewScope } from './api';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;
export type AskChat = { text: string; reference: StorefrontConversations };

// The chip's words: the server takes at most 200 characters, and a shop's name (from its own
// page) can be longer.
const LABEL_MAX = 200;
const chip = (label: string) =>
  label.length > LABEL_MAX ? `${label.slice(0, LABEL_MAX - 1)}…` : label;

/** One conversation: `when` is when it started, as the list shows it. */
export const conversationAsk = (
  copy: Copy,
  store: { id: string; name: string },
  conversationId: string,
  when: string,
): AskChat => ({
  text: copy.askConversationMessage,
  reference: {
    store: store.id,
    conversation: conversationId,
    label: chip(formatCopy(copy.conversationsChipOne, { name: store.name, when })),
  },
});

/**
 * The week, for one assistant (its market, when one is picked) or all of them. `name` is the
 * assistant's name, or "All assistants".
 */
export function weekAsk(
  copy: Copy,
  scope: OverviewScope,
  name: string,
  market: { code: string; name: string } | null,
): AskChat {
  const label = market
    ? formatCopy(copy.conversationsChipWeekIn, { name, market: market.name })
    : formatCopy(copy.conversationsChipWeek, { name });
  return {
    text: copy.askWeekMessage,
    reference: {
      store: scope.store === 'all' ? null : scope.store,
      ...(market ? { market: market.code } : {}),
      label: chip(label),
    },
  };
}
